import { message } from 'antd';
import userAvatar from 'assets/imgs/admin.webp';
import appLogoDark from 'assets/logos/logo-white.svg';
import appLogo from 'assets/logos/logo.svg';
import axios from 'axios';
import clsx from 'clsx';
import AppMainNavigationMenu from 'components/AppMainNavigationMenu';
import AppMainNavigationTabBar from 'components/AppMainNavigationTabBar';
import AppUserMenu from 'components/AppUserMenu';
import ScrollRestoration from 'components/ScrollRestoration';
import SplashScreen from 'components/SplashScreen';
import { darkModeSelector } from 'features/app-info/selectors';
import { TOKEN } from 'features/auth/constants';
import { getAuthToken } from 'features/auth/helpers';
import { currentUserSelector } from 'features/auth/selectors';
import { setUserInfo } from 'features/auth/slice';
import { TUserInfo } from 'features/auth/types';
import { KeycloakRequestBaseURL } from 'lib/keycloak/keycloak';
import { useAppDispatch, useAppSelector } from 'lib/stores/hook';
import { memo, Suspense, useCallback, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getPath } from 'routes/router-paths';

const MainLayout = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [messageApi, contextHolder] = message.useMessage();

  const currentUser = useAppSelector(currentUserSelector);
  const isDarkModeEnabled = useAppSelector(darkModeSelector);

  const { preferred_username } = currentUser || {};

  const refreshToken = getAuthToken(TOKEN.REFRESH_TOKEN);
  const accessToken = getAuthToken(TOKEN.ACCESS_TOKEN);

  useEffect(() => {
    if (refreshToken) return;
    navigate(getPath('loginPage', location.pathname));
  }, [refreshToken, location.pathname, messageApi, navigate]);

  const fetchUserInfo = useCallback(async () => {
    try {
      const userData = await axios.request<unknown, TUserInfo>({
        url: `${KeycloakRequestBaseURL}userinfo`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      dispatch(setUserInfo(userData));
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  }, [accessToken, dispatch]);

  useEffect(() => {
    void fetchUserInfo();
  }, [fetchUserInfo]);

  return (
    <div className="flex h-screen gap-x-4 bg-[#e5e5e5] p-4 pr-0 dark:bg-[#141A21]">
      <aside className="w-65 shrink-0 overflow-y-auto overflow-x-hidden rounded-2xl bg-white pb-4 pt-5.5 dark:bg-[--bg-dark]">
        <div className="flex items-center justify-between px-4">
          <figure
            className="mb-0 cursor-pointer"
            onClick={() => navigate(getPath('portalPage'))}
          >
            <img
              src={isDarkModeEnabled ? appLogoDark : appLogo}
              alt="VIMC"
              width={100}
            />
          </figure>

          <AppUserMenu />
        </div>

        <div className="relative flex flex-col items-center justify-center py-6 text-center transition-all">
          <figure className="relative">
            <img
              src={userAvatar}
              alt="User avatar"
              className={clsx('block size-20 rounded-full object-cover')}
            />
            {preferred_username && (
              <figcaption
                className={clsx(
                  'absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2',
                  'rounded-full bg-primary px-2 py-1',
                  'whitespace-nowrap text-xs uppercase text-white'
                )}
              >
                {preferred_username}
              </figcaption>
            )}
          </figure>
        </div>

        <nav role="navigation" className="px-1">
          <AppMainNavigationMenu />
        </nav>
      </aside>

      <div className="mx-auto flex w-full flex-1 flex-col overflow-y-auto overflow-x-hidden pr-4">
        <AppMainNavigationTabBar />

        <Suspense
          fallback={
            <ScrollRestoration>
              <SplashScreen fullScreen />
            </ScrollRestoration>
          }
        >
          <Outlet />
        </Suspense>
      </div>
      {contextHolder}
    </div>
  );
};

export default memo(MainLayout);
